import React from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Formik, useFormikContext } from 'formik';
import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  Button,
  Link,
  Media,
  ShareButtonGroup,
  Credit,
  primaryOutlinedButtonSx,
} from '@elevator/ui';
import { Service } from '@elevator/api-hooks/types/schema-types';
import { SubmissionStatus } from 'atoms';
import { DOMAIN_BASE, ROUTES } from 'config/Nav';
import SUBMISSION_CONTENT_TYPES from 'config/SubmissionContentTypes';
import SUBMISSION_STATUSES from 'config/SubmissionStatuses';
import { useEditSubmission } from 'hooks/useEditSubmission';
import {
  CreditGrid,
  OrderDetails,
  PageHeader,
  SectionHeader,
  SubmissionForm,
} from 'molecules';
import type { OrderDetailsProps } from 'molecules/OrderDetails';
import {
  Submission as SubmissionType,
  SubmissionContent,
  SubmissionContentType,
} from 'types/submissions';
import { CreditInput } from 'types/credit';
import useStyles from './styles';

interface EditSubmissionContentProps {
  orderDetailsProps: OrderDetailsProps;
  creditList: CreditInput[];
  removeCredit: (creditId: string | undefined, index: number) => Promise<void>;
  saveCredit: (credit: CreditInput, index: number) => Promise<void>;
  returnToSubmissions: () => void;
  statusLabel: string;
}

function EditSubmissionContent({
  orderDetailsProps,
  creditList,
  removeCredit,
  saveCredit,
  returnToSubmissions,
  statusLabel,
}: EditSubmissionContentProps): React.JSX.Element {
  const { isValid, isSubmitting } = useFormikContext<SubmissionContent>();
  return (
    <>
      <OrderDetails {...orderDetailsProps} />
      <SubmissionForm
        credits={creditList}
        removeCredit={removeCredit}
        saveCredit={saveCredit}
        canAddCredits={statusLabel === 'Review' || statusLabel === 'Approval'}
        submitLabel="Save"
        cancelLabel="Cancel"
        onCancel={returnToSubmissions}
        submitDisabled={!isValid || isSubmitting}
      />
    </>
  );
}

/**
 * Submission Component
 *
 * Displays a single submission with the ability to edit or view submission details
 * depending on the submission status. Renders either an editable form or a read-only
 * view of the submission content.
 *
 * Features:
 * - Conditionally renders OrderDetails + SubmissionForm (via useEditSubmission) for submissions in Review/Approval status.
 * - Displays read-only view for completed or failed submissions.
 * - Shows submission content with link, description, and image.
 * - Renders credits grid for submission collaborators.
 * - Displays live submission block with share buttons for completed submissions.
 * - Handles submission updates via GraphQL mutation.
 *
 * Used in:
 * - `Submission` layout index file.
 *
 * @param props - Component props
 * @param props.submission - The submission data object.
 * @returns Submission component with edit or view mode
 */
const Submission = ({ submission }: SubmissionProps): React.JSX.Element => {
  const { classes } = useStyles();

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    contentTitle,
    contentUrl,
    status,
    flagged,
    contentDescription,
    contentImage,
    credits,
    submissionId,
    orderNumber,
    submissionType,
    services,
    order,
  } = submission;

  const statusLabel = flagged ? 'Flagged' : status;

  const router = useRouter();

  /**
   * Navigates back to the submissions list page
   */
  const returnToSubmissions = (): void => {
    router.push(ROUTES.ACCOUNT.SUBMISSIONS.to);
  };

  const editProps = useEditSubmission({ submission, returnToSubmissions });

  /**
   * Filters services to get only add-on services (services with a parent)
   *
   * @returns Array of add-on services
   */
  const addOns: Service[] = services.filter((service) => service.parent);

  /**
   * Returns post URL using domain base and slug
   *
   * @returns Full URL to the published post
   */
  const getPostUrl = (): string => {
    const { slug } = submission.post;
    return `${DOMAIN_BASE}/${slug}`;
  };

  /**
   * Renders the share button group for the published post
   *
   * @returns ShareButtonGroup component
   */
  const renderShareButtonGroup = (): React.JSX.Element => {
    const postUrl = getPostUrl();
    return <ShareButtonGroup shareLink={postUrl} size="large" postTitle={contentTitle} />;
  };

  /**
   * Renders a submission section item based on its type (link, image, or string)
   *
   * @param fieldName - The label for the field
   * @param type - The type of content (link, image, or string)
   * @param value - The value to display
   * @returns Rendered submission section item
   */
  const renderSubmissionSectionItem = (
    fieldName: string,
    type: SubmissionContentType,
    value: string | Credit[] | Media,
  ): React.JSX.Element => {
    let content;

    switch (type) {
      case SUBMISSION_CONTENT_TYPES.LINK:
        content = (
          <Link href={value as string} className={classes.link}>
            <Typography variant="body1" className={classes.defaultLink}>
              {value as string}
            </Typography>
          </Link>
        );
        break;
      case SUBMISSION_CONTENT_TYPES.IMAGE:
        if (!contentImage) {
          content = <Typography>(None provided)</Typography>;
        } else {
          const imageMedia = value as Media;
          content = (
            <Image
              src={imageMedia.sourceUrl}
              className={classes.image}
              width={800}
              height={600}
              alt="Submission Image"
              style={{ width: '100%', height: 'auto' }}
            />
          );
        }
        break;
      case SUBMISSION_CONTENT_TYPES.STRING:
      default:
        content = <Typography>{value as string}</Typography>;

        break;
    }

    return (
      <Box className={classes.submissionSection}>
        <Typography variant="caption" color="textSecondary">
          {fieldName}
        </Typography>
        <Box>{content}</Box>
      </Box>
    );
  };

  /**
   * Renders the credits section with credits grid
   *
   * @param value - Array of credits to display
   * @returns Credits section component
   */
  const renderCredits = (value: Credit[]): React.JSX.Element => (
    <Box className={classes.credits}>
      <SectionHeader title="Credits" titleVariant="h3" divider={false} />
      <Box className={classes.creditContainer}>
        {value && value.length > 0 ? (
          <CreditGrid credits={value} />
        ) : (
          <Typography color="textSecondary">No credits.</Typography>
        )}
      </Box>
    </Box>
  );

  /**
   * Renders the live submission block with share buttons for completed submissions
   *
   * @returns Live submission UI
   */
  const renderLiveSubmission = (): React.JSX.Element => {
    const postUrl = getPostUrl();
    return (
      <Box className={classes.submissionLivePost}>
        <Typography variant="h5" gutterBottom>
          Your post is live! 🤝
        </Typography>
        <Typography variant="body1" component="span" gutterBottom>
          &quot;{contentTitle}&quot; is now live at{' '}
          <a href={postUrl} className={classes.link}>
            {postUrl}
          </a>
          . Make sure to share the news with your followers on social media!
        </Typography>
        <Box className={classes.livePostInner}>{renderShareButtonGroup()}</Box>
      </Box>
    );
  };

  /**
   * Renders submission content details (link, description, image)
   *
   * @returns Submission content detail sections
   */
  const renderSubmissionContentDetails = (): React.JSX.Element => (
    <>
      {renderSubmissionSectionItem(
        'Content Link',
        SUBMISSION_CONTENT_TYPES.LINK,
        contentUrl,
      )}
      {renderSubmissionSectionItem(
        'Description',
        SUBMISSION_CONTENT_TYPES.STRING,
        contentDescription,
      )}
      {renderSubmissionSectionItem(
        'Image',
        SUBMISSION_CONTENT_TYPES.IMAGE,
        contentImage as Media,
      )}
    </>
  );

  return (
    <Box className={classes.container} data-layout="submission">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs aria-label="breadcrumb">
            <Link href={ROUTES.ACCOUNT.SUBMISSIONS.to}>My Submissions</Link>
            <Typography color="text.primary">Order #{orderNumber}</Typography>
          </Breadcrumbs>
        }
        title="Submission"
        actions={
          <SubmissionStatus
            variant={SUBMISSION_STATUSES[statusLabel.toUpperCase()]}
            orderStatus={order.status}
            orderFlagged={order.flagged}
          />
        }
        divider
      />
      {(() => {
        const isEditable =
          (submission.status === 'Review' || submission.status === 'Approval') &&
          order.status !== 'failed' &&
          order.flagged === false;

        if (isEditable) {
          return (
            <Formik
              enableReinitialize
              key={submissionId}
              initialValues={editProps.initialValues}
              onSubmit={editProps.onSubmit}
              validationSchema={editProps.validationSchema}
            >
              <EditSubmissionContent
                orderDetailsProps={editProps.orderDetailsProps}
                creditList={editProps.creditList}
                removeCredit={editProps.removeCredit}
                saveCredit={editProps.saveCredit}
                returnToSubmissions={returnToSubmissions}
                statusLabel={statusLabel}
              />
            </Formik>
          );
        }

        // View Mode
        return (
          <Box className={classes.viewStack}>
            <OrderDetails
              submissionType={submissionType}
              addOns={addOns}
              orderNumber={orderNumber}
              orderDate={submission.createdAt}
              submissionNumber={submissionId}
              total={order.paymentType === 'Free' ? 0 : order.amountPaid}
              paymentType={order.paymentType}
            />
            <Box className={classes.submissionDetails}>
              <SectionHeader
                title="Submission Details"
                titleVariant="h2"
                divider={false}
              />
              <Box className={classes.content}>
                <SectionHeader title="Content" titleVariant="h3" divider={false} />
                <Box className={classes.titleGroup}>
                  <Typography variant="caption" color="textSecondary">
                    Submission Title
                  </Typography>
                  <Typography variant="body1" className={classes.contentTitle}>
                    {contentTitle}
                  </Typography>
                </Box>
                <>
                  {statusLabel === SUBMISSION_STATUSES.COMPLETED &&
                    renderLiveSubmission()}
                  {renderSubmissionContentDetails()}
                </>
              </Box>
              {/* Credits */}
              {renderCredits(credits)}
              {/* Disabled Checkbox */}
              <Box className={classes.agreement}>
                <SectionHeader title="Agreement" titleVariant="h3" divider={false} />
                <Box className={classes.agreementRow}>
                  <Checkbox
                    color="secondary"
                    checked
                    required
                    disabled
                    name="sponsorChecked"
                  />
                  <Typography align="left" color="textSecondary" display="inline">
                    I certify that I have the expressed permission or authority to submit
                    this content. *
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.closeRow}>
                <Button
                  onClick={returnToSubmissions}
                  variant="outlined"
                  sx={primaryOutlinedButtonSx}
                  fullWidth={mobile}
                >
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
        );
      })()}
    </Box>
  );
};

export interface SubmissionProps {
  submission: SubmissionType;
}

export default Submission;
